import jsPDF from 'jspdf';
import { MonthlyGoalReport } from './storeSalesGoalsService';

export class SimplePDFReportService {
  private static instance: SimplePDFReportService;

  static getInstance(): SimplePDFReportService {
    if (!SimplePDFReportService.instance) {
      SimplePDFReportService.instance = new SimplePDFReportService();
    }
    return SimplePDFReportService.instance;
  }

  private formatMonthYear(monthYear: string): string {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  async generateSimpleStoreGoalReportPDF(report: Omit<MonthlyGoalReport, 'id'>, monthYear: string): Promise<Blob> {
    try {
      console.log('📄 SimplePDFReportService: Starting simple PDF generation...');
      
      const doc = new jsPDF();
      const monthName = this.formatMonthYear(monthYear);
      let yPosition = 20;
      
      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Chorost & Co. - Store Goals Report', 20, yPosition);
      yPosition += 15;
      
      // Month
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report Period: ${monthName}`, 20, yPosition);
      yPosition += 10;
      
      // Generated date
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, yPosition);
      yPosition += 20;
      
      // Overall Summary
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Overall Performance Summary', 20, yPosition);
      yPosition += 15;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Goal: ${this.formatCurrency(report.totalGoalAmount)}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Total Sales: ${this.formatCurrency(report.totalActualSales)}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Overall Completion: ${report.overallCompletionPercentage.toFixed(1)}%`, 20, yPosition);
      yPosition += 20;
      
      // Store Performance
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Store Performance Details', 20, yPosition);
      yPosition += 15;
      
      // Sort stores by completion percentage
      const sortedGoals = [...report.goals].sort((a, b) => b.completionPercentage - a.completionPercentage);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      sortedGoals.forEach((goal, index) => {
        const rank = index + 1;
        const emoji = goal.completionPercentage >= 100 ? '🎯' :
                     goal.completionPercentage >= 80 ? '📈' :
                     goal.completionPercentage >= 60 ? '📊' : '📉';
        
        const line = `${rank}. ${goal.storeName}: ${this.formatCurrency(goal.actualSales)} / ${this.formatCurrency(goal.goalAmount)} (${goal.completionPercentage.toFixed(1)}%)`;
        
        doc.text(line, 20, yPosition);
        yPosition += 8;
        
        // Check if we need a new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
      });
      
      // Key Insights
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Insights', 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const topPerformer = sortedGoals[0];
      const achievedGoals = sortedGoals.filter(g => g.completionPercentage >= 100);
      const needsImprovement = sortedGoals.filter(g => g.completionPercentage < 80);
      
      if (topPerformer) {
        doc.text(`Top Performer: ${topPerformer.storeName} (${topPerformer.completionPercentage.toFixed(1)}%)`, 20, yPosition);
        yPosition += 8;
      }
      
      doc.text(`Stores that achieved goals: ${achievedGoals.length} of ${sortedGoals.length}`, 20, yPosition);
      yPosition += 8;
      
      if (needsImprovement.length > 0) {
        doc.text(`Stores needing improvement: ${needsImprovement.length}`, 20, yPosition);
      }
      
      console.log('✅ Simple PDF generation completed successfully');
      return doc.output('blob');
      
    } catch (error) {
      console.error('❌ Simple PDF generation failed:', error);
      throw new Error(`Simple PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async downloadSimpleStoreGoalReport(report: Omit<MonthlyGoalReport, 'id'>, monthYear: string): Promise<void> {
    try {
      const pdfBlob = await this.generateSimpleStoreGoalReportPDF(report, monthYear);
      const monthName = this.formatMonthYear(monthYear);
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Store_Goals_Report_${monthName.replace(' ', '_')}_Simple.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      console.log(`Simple PDF report downloaded: Store_Goals_Report_${monthName.replace(' ', '_')}_Simple.pdf`);
    } catch (error) {
      console.error('Error downloading simple PDF report:', error);
      throw error;
    }
  }
}

export const simplePdfReportService = SimplePDFReportService.getInstance();
